import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { executeAiRequest } from '@/api/ai-service';

const Brief = z.object({
  brief: z.string().max(1500),
  adSize: z.string().max(80).optional(),
  paper: z.string().max(80).optional(),
});

const SiteSchema = {
  type: 'object',
  properties: {
    found: { type: 'boolean', description: 'True if information about this business website was found' },
    businessName: { type: 'string' }, address: { type: 'string' }, phone: { type: 'string' },
    hours: { type: 'string' }, tagline: { type: 'string', description: 'Their slogan or a short line describing them, in their own words if possible' },
    description: { type: 'string', description: 'Two sentences describing what they offer' },
  },
  required: ['found', 'businessName', 'address', 'phone', 'hours', 'tagline', 'description'], additionalProperties: false,
} as const;

/** Reads public details (name, address, phone, hours, tagline) from the advertiser's website. */
export const readBusinessSite = createServerFn({ method: 'POST' })
  .validator((d: unknown) => z.object({ url: z.string().min(4).max(300) }).parse(d))
  .handler(async ({ data }) => {
    const url = /^https?:\/\//i.test(data.url) ? data.url : `https://${data.url}`;
    try {
      const r = await executeAiRequest(`Look up the business website ${url}. Report the business name, street address, phone number, opening hours, their tagline or slogan, and a two-sentence description, exactly as published. Use an empty string for anything you cannot find. Do not guess.`,
        { schema: SiteSchema, webSearch: { useWebSearch: true, maxResults: 5 } });
      return r.data as { found: boolean; businessName: string; address: string; phone: string; hours: string; tagline: string; description: string };
    } catch (e) {
      console.error('[adhelper] readBusinessSite failed', e);
      throw new Error('We couldn\'t read that website. You can type the details in instead.');
    }
  });

const DraftSchema = {
  type: 'object',
  properties: {
    headline: { type: 'string', description: 'Short, punchy headline (max 8 words)' },
    subhead: { type: 'string', description: 'Supporting line (max 15 words)' },
    body: { type: 'string', description: 'Main ad copy sized to the ad: Eighth/Fifth page about 25 words, Quarter about 40, Half about 60, Full about 90' },
    callToAction: { type: 'string', description: 'What readers should do, e.g. "Visit us this Saturday"' },
    details: { type: 'array', items: { type: 'string' }, description: 'Contact/info lines to print: address, phone, hours, website, event date' },
    designNotes: { type: 'string', description: 'One or two sentences of layout suggestions for the newspaper designer' },
  },
  required: ['headline', 'subhead', 'body', 'callToAction', 'details', 'designNotes'], additionalProperties: false,
} as const;

export type AdDraft = { headline: string; subhead: string; body: string; callToAction: string; details: string[]; designNotes: string };

/** Writes a draft ad from the advertiser's own words, website details and a chosen example. */
export const writeAdDraft = createServerFn({ method: 'POST' })
  .validator((d: unknown) => Brief.extend({
    words: z.string().max(2000), businessInfo: z.string().max(2000), inspiration: z.string().max(500).optional(),
  }).parse(d))
  .handler(async ({ data }) => {
    const prompt = `Write a draft print ad for a local community newspaper (${data.paper || 'Star-Revue'}). Keep the advertiser's own words and facts; do not invent prices, dates, addresses or claims they didn't give.

Ad size: ${data.adSize || 'unknown'}
Campaign brief: ${data.brief}
Business details: ${data.businessInfo}
What the advertiser wants to say (their words): ${data.words}
${data.inspiration ? `An example ad they liked: ${data.inspiration}` : ''}`;
    try {
      const r = await executeAiRequest(prompt, { schema: DraftSchema, systemPrompt: 'You are a seasoned local-newspaper ad copywriter. Clear, warm, neighborly, no hype.' });
      return r.data as AdDraft;
    } catch (e) {
      console.error('[adhelper] writeAdDraft failed', e);
      throw new Error('We couldn\'t write a draft right now. Please try again in a moment.');
    }
  });
